'use client';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Deferred, {Promise} from 'promise-deferred';
import {KEIConnectOptions} from '../types';
import {
  JsonRpcPayload,
  JsonRpcProvider,
  JsonRpcResult,
  JsonRpcSigner,
} from 'ethers';
import {Account, User} from '@kei.fi/data';
import {ConnectRpcProvider} from '../signer/provider-connect';
import {useIframe} from '../hooks';
import {Subject} from 'rxjs';
import {useWeb3Connect, Web3ConnectionType} from '@kei.fi/web3-connect';
import {useConnectToChild} from '../hooks/useConnectToChild';

export enum KEIConnectMethod {
  Web3 = 'Web3',
  Account = 'Account',
}

export interface KEIConnectContext {
  connect(): Promise<void>;

  disconnect(): Promise<void>;

  account?: Account | string;
  user?: User;
  chainId?: number;

  signer?: JsonRpcSigner;
  connectMethod?: KEIConnectMethod;

  isConnected: boolean;

  isConnecting: boolean;

  /**
   * In order for this domain to be able to execute transactions then it must be a trusted domain.
   * In the scneario it is not trusted, there will be no signer.
   */
  isTrustedDomain: boolean;

  /**
   * The signer becomes locked when they code times out, or after every transaction if specified to behave this way.
   * in this scenario they would have to re-enter their pin code on the next transaction
   */
  isSignerUnlocked: boolean;

  isAuthenticated: boolean;

  isInitialising: boolean;
}

export interface KEIConnectProviderProps {
  options: KEIConnectOptions;
}

export const KEIConnectContext = createContext<KEIConnectContext>(null!);

export const KEIConnectProvider = ({
  children,
  options,
}: PropsWithChildren<KEIConnectProviderProps>) => {
  const web3 = useWeb3Connect();
  const cancelSubject = new Subject<void>();
  const readyPromise = useRef(new Deferred<void>());
  const pipedPromiseRef = useRef(Promise.resolve({} as any));
  const [connecting, setConnecting] = useState(0);
  const [connectMethod, setConnectMethod] = useState<
    KEIConnectMethod | undefined
  >(undefined);
  const [isInitialising, setIsInitialising] = useState<boolean>(true);
  const [isTrustedDomain, setIsTrustedDomain] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isSignerUnlocked, setIsSignerUnlocked] = useState<boolean>(false);
  const [account, setAccount] = useState<Account | undefined>(undefined);
  const [chainId, setChainId] = useState<number | undefined>(undefined);
  const [user, setUser] = useState<User | undefined>(undefined);
  const [signer, setSigner] = useState<JsonRpcSigner | undefined>(undefined);
  const iframe = useIframe();

  const isConnected =
    connectMethod === KEIConnectMethod.Web3
      ? web3.state.isActive && !!web3.state.account
      : account !== undefined;

  const currentChainId =
    connectMethod === KEIConnectMethod.Web3 ? web3.state.chainId : chainId;

  const isConnecting = connecting > 0;

  useEffect(() => {
    iframe.init(options.url);
  }, [options.url]);

  const childPromise = useConnectToChild(iframe.element, {
    show: useCallback(() => iframe.show(), []),
    hide: useCallback(() => iframe.hide(), []),
    ready: useCallback(() => {
      readyPromise.current.resolve();
      setIsInitialising(false);
      cancelSubject.next();
      iframe.hide();
    }, []),
    web3RequestSignature: useCallback(
      async (data: string) => {
        const signer = await web3.state.provider?.getSigner(web3.state.account);
        return signer?.signMessage(data);
      },
      [web3.state]
    ),
    updateWeb3ConnectionType: useCallback(
      (connectionType: Web3ConnectionType) =>
        web3.setConnectionType(connectionType),
      [web3.setConnectionType]
    ),
    updateAccount: useCallback(
      (account: Account | undefined) => setAccount(account),
      []
    ),
    updateChainId: useCallback((chainId: number) => setChainId(chainId), []),
    updateIsAuthenticated: useCallback(
      (authenticated: boolean) => setIsAuthenticated(authenticated),
      []
    ),
    updateConnectMethod: useCallback(
      (method: KEIConnectMethod | undefined) => setConnectMethod(method),
      []
    ),
    updateIsTrustedDomain: useCallback(
      (trusted: boolean) => setIsTrustedDomain(trusted),
      []
    ),
    updateIsSignerUnlocked: useCallback(
      isSignerUnlocked => setIsSignerUnlocked(isSignerUnlocked),
      []
    ),
    updateUser: useCallback((user: User | undefined) => setUser(user), []),
  });

  useEffect(() => {
    iframe.mount();
    return () => {
      iframe.unmount();
    };
  }, []);

  useEffect(() => {
    childPromise.then(child => {
      const {provider, connector, ...state} = web3.state;
      return child.updateWeb3State({
        ...state,
      });
    });
  }, [web3.state, childPromise]);

  const connect = useCallback(async () => {
    setConnecting(current => current + 1);
    const child = await childPromise;
    await wrapPipedPromise(async () => {
      try {
        iframe.show();
        await child.connect();
      } catch (e) {
        // eslint-disable-next-line no-debugger
        console.log('ERROR', e);
      }
      iframe.hide();
    });
    setConnecting(current => current - 1);
  }, [childPromise]);

  const wrapPipedPromise = <T,>(logic: () => Promise<T> | T): Promise<T> => {
    const generator = () =>
      new Promise((resolve, reject) => {
        const subscription = cancelSubject.subscribe(() => {
          reject(new Error('CANCELLED'));
        });

        resolve(logic());

        subscription.unsubscribe();
      });

    return (pipedPromiseRef.current = pipedPromiseRef.current
      .catch(() => Promise.resolve())
      .then(() => readyPromise.current.promise)
      .then(generator));
  };

  const signerSend = useCallback(
    async (
      payload: JsonRpcPayload | JsonRpcPayload[]
    ): Promise<JsonRpcResult[]> => {
      iframe.show();
      try {
        const child = await childPromise;

        const result = await wrapPipedPromise(() => {
          return child.signerSend(payload);
        });

        console.log('RESULT');
        iframe.hide();
        return result;
      } catch (e) {
        // eslint-disable-next-line no-debugger
        console.log('ERROR', e);
        iframe.hide();
        throw e;
      }
    },
    [childPromise]
  );

  const disconnect = useCallback(async (): Promise<void> => {
    try {
      const child = await childPromise;
      await wrapPipedPromise(() => child.disconnect());
    } catch (e) {
      console.error(e);
      throw e;
    }
  }, [childPromise]);

  useEffect(() => {
    if (connectMethod === KEIConnectMethod.Web3) {
      if (!web3.state.provider || !web3.state.account) {
        setSigner(undefined);
        return;
      }

      web3.state.provider.getSigner(web3.state.account).then(signer => {
        setSigner(signer);
      });
    } else {
      if (!chainId || !isTrustedDomain) {
        setSigner(undefined);
        return;
      }

      const address =
        account?.deployments[chainId]?.address || web3.state.account;
      if (!address) {
        setSigner(undefined);
        return;
      }

      const providerUrl = options.chainUrls[chainId]?.[0];
      if (!providerUrl) {
        throw new Error('cannot find provider url for chain ' + chainId);
      }

      const provider = web3.state.provider || new JsonRpcProvider(providerUrl);

      provider.getNetwork().then(network => {
        setSigner(
          new JsonRpcSigner(
            new ConnectRpcProvider(signerSend, provider, network),
            address
          )
        );
      });
    }
  }, [
    signerSend,
    account,
    chainId,
    isTrustedDomain,
    web3.state.provider,
    web3.state.account,
  ]);

  return (
    <KEIConnectContext.Provider
      value={{
        connect,
        account,
        disconnect,
        chainId: currentChainId,
        signer,
        user,
        connectMethod,
        isConnected,
        isAuthenticated,
        isInitialising,
        isTrustedDomain,
        isSignerUnlocked,
        isConnecting,
      }}
    >
      {children}
    </KEIConnectContext.Provider>
  );
};
